import Spacer from '@berlitz/spacer'
import PortalLearningPath from '@classes/LearningPath'
import PortalLearningPathUnit from '@classes/LearningPathUnit'
import PortalStudentProfile from '@classes/StudentProfile'
import Collapse from '@components/Collapse'
import ElectiveRequiredBanner from '@components/ElectiveRequiredBanner'
import EmptyLearningPathV2 from '@components/EmptyLearningPathV2'
import SessionPanel from '@components/LPCard/SpeakingPractice/SessionPanel'
import PreparingNxtLvlBanner from '@components/PreparingNxtLvlBanner'
import { StoreProps } from '@interfaces/StoreState'
import { RegistrationMaterial, RegistrationWrapper } from '@interfaces/Student'
import CollapseHeader from '@layouts/LearningPath/Layout/CollapseHeader'
import ProgressNav from '@layouts/LearningPath/Layout/ProgressNav'
import {
  MaintenanceBannerContainer,
  OverrideLPEndOfModuleCard,
  TileCol,
  TileRow,
} from '@layouts/LearningPath/Layout/style'
import ProgramContent from '@layouts/LearningPath/ProgramContent'
import { ELECTIVE, FLEX_LEVEL_STATUS, FLEX_VERSION } from '@utils/constants'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import CTPanelPath from './CTPanelPath'
import PanelTile from './PanelTile'
import EmptyLearningPathV3 from '@components/EmptyLearningPathV3'
import GenericErrorBoundary from '@hocs/ErrorBoundary/GenericErrorBoundary'
import LPEndOfModuleCard from '@berlitzplatforms/micro.ui.lp-end-of-module-card'
import { useLPContext } from './Context/LPContext'

export interface PanelPathProps {
  Path: PortalLearningPath | undefined
  Registration: RegistrationWrapper | undefined
  showPreparingBanner?: boolean
  isCT?: boolean
  StudentProfile?: PortalStudentProfile
  selectedModule?: number
  regMaterial?: RegistrationMaterial
  hasSessionPanel?: boolean
}

const PathPanel: React.FC<PanelPathProps> = ({
  Path,
  Registration,
  isCT,
  StudentProfile,
  selectedModule,
  regMaterial,
  hasSessionPanel = true,
}) => {
  const intl = useIntl()
  const { userPermissions, activeRegistrationId, activeProgramId } = useSelector(
    ({ permissions, dashboard }: StoreProps) => ({
      userPermissions: permissions.permissions,
      activeRegistrationId: dashboard.activeRegistration ?? '',
      activeProgramId: dashboard.activeProgram ?? '',
    })
  )
  const skillTutorLevel = Path?.Level?.[0] != null ? (Path.Level[0] < 5 ? 'Basic' : 'Intermediate') : undefined
  const { setLPModule } = useLPContext()
  const AYCLFlag = userPermissions['flag::AYCL-feature']?.show
  const CTFlag = userPermissions['flag::CT-feature']?.show
  const showPrepariingNextLevel = (reg) =>
    AYCLFlag &&
    [FLEX_VERSION.TWO, FLEX_VERSION.MULTI].indexOf(reg?.RegistrationInfo?.FlexVersion) >= 0 &&
    reg?.RegistrationInfo?.FlexLevelStatus === FLEX_LEVEL_STATUS.LEVEL_STARTED

  const Units = useMemo(() => {
    if (!selectedModule || !Path || !Path.Modules || !Path.Modules.length) {
      // when there's no module for the lp
      return Path?.NonCultureUnits
    } else if (Path.Modules.indexOf(selectedModule) > -1) {
      // when lp has modules and module value exist
      return Path?.NonCultureUnits?.filter((unit) => unit.module === selectedModule)
    } else if (Path.Modules.indexOf(selectedModule) === -1) {
      // very unlikely to happen but when module value exceeds the modules of the lp, use the highest value
      return Path?.NonCultureUnits?.filter((unit) => unit.module === Path!.Modules![Path!.Modules!.length - 1])
    }
  }, [selectedModule, Path])

  const hasModules = selectedModule && Path?.Modules?.length
  const isEndOfModule = useMemo(() => {
    if (!hasModules) {
      return false
    }

    return Path.Modules?.[Path.Modules.length - 1] === selectedModule
  }, [hasModules, selectedModule])
  return (
    <GenericErrorBoundary
      fallback={
        <Collapse.Panel
          key={Path?.Material?.ElectiveMaterials?.[0]?.LPID}
          id={Path?.Id || Registration?.RegistrationInfo?.RegistrationId || ''}
          header={
            <CollapseHeader
              title={Path?.Material?.ElectiveMaterials?.[0]?.MaterialName || ''}
              language={Path?.Language}
              materialType={Path?.learningPath?.details?.material_type}
              isFlex={Boolean(Path?.IsFlex)}
              isSkillTutorPath={Boolean(Path?.IsSkillTutorContent)}
              level={skillTutorLevel}
            />
          }
        >
          <MaintenanceBannerContainer>
            <EmptyLearningPathV3 />
          </MaintenanceBannerContainer>
        </Collapse.Panel>
      }
    >
      {Path ? (
        isCT && CTFlag && Registration ? (
          <CTPanelPath Path={Path} Registration={Registration!} StudentProfile={StudentProfile} />
        ) : (
          <>
            <Collapse.Panel
              key={Path?.Id}
              id={Path?.Id?.toString() || 'error-boundary'}
              header={
                <CollapseHeader
                  title={Path.activeProgramNameByRegAndProgId(activeRegistrationId, activeProgramId)}
                  language={Path.Language}
                  materialType={Path.learningPath?.details?.material_type}
                  isFlex={Boolean(Path.IsFlex)}
                  path={Path}
                  isSkillTutorPath={Boolean(Path?.IsSkillTutorContent)}
                  level={skillTutorLevel}
                  isIndustrySpecific={Boolean(Path?.IsIndustrySpecificContent)}
                />
              }
            >
              {(Path.Material?.ElectiveRequired === ELECTIVE.REQUIRED && Path.Material?.ElectiveMaterials?.length) ||
              (Path.IsIndustrySpecificContent && !Path.Material?.ElectiveMaterials?.length) ||
              Path.Material?.ElectiveRequired !== ELECTIVE.REQUIRED ||
              Path.Material?.SkillsTutorContent ? (
                <>
                  <ProgramContent Path={Path} />
                  <>
                    <TileRow id={`scroller-${Path.Id}`}>
                      {Units?.map((Unit) => {
                        if (Unit.IsCoaching) {
                          Unit.Unlimited = Path.UnlimitedCoaching
                        }
                        return <PanelTile Unit={Unit as PortalLearningPathUnit} Path={Path} key={Unit?.Id} />
                      })}
                      {hasModules ? (
                        <TileCol className="test">
                          <OverrideLPEndOfModuleCard>
                            <LPEndOfModuleCard
                              title={intl.formatMessage({ id: 'Well done!', defaultMessage: 'Well done!' })}
                              subtitle={intl.formatMessage({
                                id: `Look at all you've completed! Keep up the good work!`,
                                defaultMessage: `Look at all you've completed! Keep up the good work!`,
                              })}
                              btnLabel={intl.formatMessage({ id: 'Next', defaultMessage: 'Next' })}
                              hideBtn={isEndOfModule}
                              onBtnClick={() => setLPModule(selectedModule + 1, StudentProfile!.SFId, Path.Id)}
                            />
                          </OverrideLPEndOfModuleCard>
                        </TileCol>
                      ) : (
                        <></>
                      )}
                    </TileRow>
                    <ProgressNav key={selectedModule} Units={Units!} scrollerId={`scroller-${Path.Id}`} />
                    {(!Path.EnrolmentCompleted || Path.NextSessionUnit?.IsBooked) &&
                    hasSessionPanel &&
                    Path.IsFlex &&
                    !Path.IsJapanFlex &&
                    !Path.IsOnDemand &&
                    !Path.IsPreviousLP &&
                    !Path.HasContractEnded ? (
                      <SessionPanel Path={Path} />
                    ) : null}
                  </>
                </>
              ) : (
                <>
                  <MaintenanceBannerContainer>
                    <ElectiveRequiredBanner Path={Path} />
                  </MaintenanceBannerContainer>
                  <Spacer size="lg" />
                </>
              )}
            </Collapse.Panel>
          </>
        )
      ) : regMaterial?.IsElective ? (
        <>
          <Collapse.Panel
            key={Registration?.RegistrationInfo.RegistrationId}
            id={(Registration?.RegistrationInfo.RegistrationId || '').toString()}
            header={
              <CollapseHeader
                title={regMaterial.MaterialName || ''}
                language={Registration?.RegistrationInfo.Language}
                isElective={regMaterial.IsElective}
              />
            }
          >
            <MaintenanceBannerContainer>
              <EmptyLearningPathV2 title={intl.formatMessage({ id: `Your elective isn't quite ready yet.` })} />
            </MaintenanceBannerContainer>
            <Spacer size="lg" />
          </Collapse.Panel>
        </>
      ) : (
        <>
          <Collapse.Panel
            key={Registration?.RegistrationInfo.RegistrationId}
            id={(Registration?.RegistrationInfo.RegistrationId || '').toString()}
            header={
              <CollapseHeader
                title={Registration?.RegistrationInfo.Materials?.[0]?.MaterialName || ''}
                language={Registration?.RegistrationInfo.Language}
              />
            }
          >
            {showPrepariingNextLevel(Registration) ? (
              <>
                <MaintenanceBannerContainer>
                  <PreparingNxtLvlBanner />
                </MaintenanceBannerContainer>
                <Spacer size="lg" />
              </>
            ) : (
              <>
                <MaintenanceBannerContainer>
                  <EmptyLearningPathV2 />
                </MaintenanceBannerContainer>
                <Spacer size="lg" />
              </>
            )}
          </Collapse.Panel>
        </>
      )}
    </GenericErrorBoundary>
  )
}

export default React.memo(PathPanel)
